import { useEffect, useState } from "react";
import {
  Input,
  Button,
  FormField,
  Select,
  SpaceBetween,
  Checkbox,
  Form,
  Container,
  ColumnLayout,
} from "@cloudscape-design/components-themed";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../app/hook";
import { isKatakanaValid, isLengthValid } from "../../utils/inputValidation";
import { useGetDivisionQuery } from "../../features/api";
import { getMessage } from "../../features/error/messages";
import { createSearchCondition } from "../../features/slices/gw-search";
import { Division } from "../../features/api-interfaces";
import {
  DivisionDict,
  getDivisionItems,
  COMMON_LABELS,
} from "../default-constants";
import {
  LABELS,
  CONTRACT_STATUS_LIST,
  SELECT_BLANK,
  PLACE_HOLDERS,
} from "./constants";
const addBlank = (list: DivisionDict[]) => {
  if (list) list.unshift(SELECT_BLANK as DivisionDict);
  return list;
};

const SearchBar = () => {
  // 事業者名
  const [companyName, setCompanyName] = useState("");
  const [companyNameErrorMsg, setCompanyNameErrorMsg] = useState("");

  // 事業者名カナ
  const [companyNameKana, setCompanyNameKana] = useState("");
  const [companyNameKanaErrorMsg, setCompanyNameKanaErrorMsg] = useState("");

  // サービス名
  const [serviceName, setServiceName] = useState({ value: "" });

  // サービス名一覧
  const [serviceNameList, setServiceNameList] = useState<DivisionDict[]>();

  // 契約状態
  const [contractStatus, setContractStatus] = useState({ value: "" });

  // 接続環境未承認
  const [connection, setConnection] = useState(false);

  // 実装確認未承認
  const [implement, setImplement] = useState(false);

  // IPID払出しルール未定義
  const [ipId, setIpId] = useState(false);

  // エラーメッセージ
  const [errText, setErrText] = useState<string>();

  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  // 共通データを取得
  // 区分取得API呼び出し(親区分コード：3:契約サービス区分コード)
  const divisionData1 = useGetDivisionQuery("3");
  const contractServiceDivisions = divisionData1?.data as Division[];
  const contractServiceKbnItems: DivisionDict[] = addBlank(
    getDivisionItems(contractServiceDivisions)
  );

  useEffect(() => {
    setServiceNameList(contractServiceKbnItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [divisionData1]);

  // 検索ボタンイベント
  const handleOnSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    // イベントキャンセル
    e.preventDefault();

    // Validate
    if (
      !checkCompanyName(companyName) ||
      !checkCompanyNameKana(companyNameKana)
    )
      return;

    dispatch(
      createSearchCondition({
        gwCompanyName: companyName,
        gwCompanyNameKana: companyNameKana,
        contractServiceCode: Number(serviceName.value),
        contractStoppedFlag: Number(contractStatus.value),
        connectionEnvBeforeApprovalFlag: Number(connection),
        impleConfirmationBeforeApprovalFlag: Number(implement),
        priorPayoutIpidRuleUnsetFlag: Number(ipId),
        timestamp: new Date().getTime(),
      })
    );
    // エラーメッセージクリア
    setErrText("");
  };

  // 事業者名バリデーションチェック
  const checkCompanyName = (companyName: string): boolean => {
    if (!isLengthValid(companyName, 30)) {
      setCompanyNameErrorMsg(getMessage("KGRERR058", 30).label);
      return false;
    } else {
      setCompanyNameErrorMsg("");
      return true;
    }
  };

  // 事業者カナバリデーションチェック
  const checkCompanyNameKana = (companyNameKana: string): boolean => {
    if (!isKatakanaValid(companyNameKana)) {
      setCompanyNameKanaErrorMsg(getMessage("KGRERR026").label);
      return false;
    } else if (!isLengthValid(companyNameKana, 45)) {
      setCompanyNameKanaErrorMsg(getMessage("KGRERR058", 45).label);
      return false;
    } else {
      setCompanyNameKanaErrorMsg("");
      return true;
    }
  };

  return (
    <form onSubmit={handleOnSubmit}>
      <Form
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="normal">{COMMON_LABELS.BTNS.SEARCH}</Button>
          </SpaceBetween>
        }
        errorText={errText}
      >
        <Container
          header={
            <Button
              onClick={() => {
                navigate("/gateway-companies/regist");
              }}
              formAction="none"
            >
              {LABELS.BTN_REGISTER}
            </Button>
          }
        >
          <SpaceBetween direction="vertical" size="l">
            <ColumnLayout columns={4}>
              <FormField
                label={LABELS.COMPANY_NAME}
                errorText={companyNameErrorMsg}
                constraintText="30文字以内"
              >
                <Input
                  name="companyName"
                  value={companyName}
                  placeholder={PLACE_HOLDERS.CORP_NAME}
                  onChange={(e) => {
                    setCompanyName(e.detail.value);
                    checkCompanyName(e.detail.value);
                  }}
                />
              </FormField>
              <FormField
                label={LABELS.COMPANY_NAME_KANA}
                errorText={companyNameKanaErrorMsg}
                constraintText="全角カナ45文字以内"
              >
                <Input
                  name="companyNameKana"
                  value={companyNameKana}
                  placeholder={PLACE_HOLDERS.CORP_KANA}
                  onChange={(e) => {
                    setCompanyNameKana(e.detail.value);
                    checkCompanyNameKana(e.detail.value);
                  }}
                />
              </FormField>
            </ColumnLayout>
            <ColumnLayout columns={4}>
              <FormField label={LABELS.SERVICE_NAME}>
                <Select
                  selectedOption={serviceName}
                  options={serviceNameList}
                  filteringType="none"
                  onChange={(event: any) => {
                    setServiceName(event.detail.selectedOption);
                  }}
                />
              </FormField>
              <FormField label={LABELS.CONTRACT_STATUS}>
                <Select
                  selectedOption={contractStatus}
                  options={CONTRACT_STATUS_LIST}
                  filteringType="none"
                  onChange={(event: any) => {
                    setContractStatus(event.detail.selectedOption);
                  }}
                />
              </FormField>
            </ColumnLayout>

            <SpaceBetween direction="horizontal" size="l">
              <FormField label={LABELS.OPERATION_PREPARATION_STATUS}>
                <SpaceBetween direction="horizontal" size="l">
                  <Checkbox
                    onChange={({ detail }) => setConnection(detail.checked)}
                    checked={connection}
                  >
                    {LABELS.CONNECTION_ENVIRONMENTAL_UNAPPROVED}
                  </Checkbox>
                  <Checkbox
                    onChange={({ detail }) => setImplement(detail.checked)}
                    checked={implement}
                  >
                    {LABELS.IMPLEMENTATION_CONFIRMATION_UNAPPROVED}
                  </Checkbox>
                  <Checkbox
                    onChange={({ detail }) => setIpId(detail.checked)}
                    checked={ipId}
                  >
                    {LABELS.IPID_PRIOR_PAYOUT_RULE_UNSET}
                  </Checkbox>
                </SpaceBetween>
              </FormField>
            </SpaceBetween>
          </SpaceBetween>
        </Container>
      </Form>
    </form>
  );
};

export default SearchBar;
